import React, { Suspense, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Box, Button, IconButton, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import PaletteIcon from "@mui/icons-material/Palette"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { Chrome, ChromeInputType, ShadeSlider, hexToHsva, hsvaToHex } from "@uiw/react-color"
import { generateCloudinaryDownloadUrl, uploadImageFromDataUrl } from "../../../utils/cloudinary"
import { updateEnterpriseLogo } from "../../../actions/Organizations/Enterprise"
import { openAppMessageSnackBar } from "../../../actions/Utils/app"
import EnterpriseLogoIconPicker, { getMaterialIconComponent } from "./EnterpriseLogoIconPicker"
import { buildTintedIconSvg } from "./iconSvgExport"

const ICON_EXPORT_ERROR_MESSAGE = "Erreur lors de la génération du logo exportable."

const INVALID_COLOR_MESSAGE = "Veuillez saisir une couleur au format hexadécimal valide."
const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/

// Preset palette: KFC brand tones, kept readable on white. No #2196F3 (CTA blue, reserved for actions).
const LOGO_COLOR_SWATCHES = [
  "#000000", "#414141", "#7C7C7C", "#151858",
  "#1045A1", "#1A74D2", "#4A008A",
  "#7900D9", "#9C3FEA", "#256F3C", "#3EA15B",
  "#48B468", "#7A5810", "#B38616", "#E8752F",
  "#E50050", "#F44559",
]

const KEEP_COLOR_REGEX = /^(none|transparent|inherit|currentcolor|#fff(fff)?|white|rgba?\(\s*255\s*,\s*255\s*,\s*255[^)]*\))$/i

const tintSvgMarkup = (src, color) => {
  let out = src.replace(/(fill|stroke)\s*=\s*"([^"]*)"/gi, (m, attr, v) => (KEEP_COLOR_REGEX.test(v.trim()) ? m : `${attr}="${color}"`))
  out = out.replace(/(fill|stroke)\s*:\s*([^;"'}]+)/gi, (m, attr, v) => (KEEP_COLOR_REGEX.test(v.trim()) ? m : `${attr}:${color}`))
  out = out.replace(/<svg(?![^>]*\sfill=)/i, `<svg fill="${color}"`)
  return out
}

const sx = {
  tile: {
    position: "relative",
    width: 245,
    height: "100%",
    flexShrink: 0,
  },
  tileContent: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  tileEmpty: {
    bgcolor: "#DCDCDC",
  },
  tileFilled: {
    bgcolor: "#FFFFFF",
    "&:hover .enterprise-media-overlay": { opacity: 1 },
  },
  tilePaletteOpen: {
    "& .enterprise-media-overlay": { opacity: 1 },
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    p: 2,
    boxSizing: "border-box",
  },
  imageTinted: {
    display: "block",
    m: 2,
    width: "calc(100% - 32px)",
    height: "calc(100% - 32px)",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
    WebkitMaskSize: "contain",
    maskSize: "contain",
  },
  imageSvg: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 2,
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    "& svg": { maxWidth: "100%", maxHeight: "100%", width: "100%", height: "100%" },
  },
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    gap: "4px",
    padding: "8px 6px 6px",
    background: "linear-gradient(transparent, rgba(0,0,0,0.30))",
    opacity: 0,
    transition: "opacity 120ms",
  },
  overlayBtn: {
    width: 28,
    height: 28,
    bgcolor: "rgba(255,255,255,0.92)",
    color: "#262626",
    "&:hover": { bgcolor: "#FFFFFF" },
  },
  overlayIcon: {
    fontSize: 16,
  },
  addButton: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    background: "none",
    border: "none",
    cursor: "pointer",
    font: "inherit",
    color: "inherit",
    p: 0,
  },
  addButtonCircle: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    bgcolor: "#8A8A8A",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    transition: "background 120ms",
    ".enterprise-media-tile:hover &": { bgcolor: "#757575" },
  },
  addButtonLabel: {
    fontSize: 16,
    fontWeight: 500,
    color: "#757575",
  },
  palette: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 5,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "6px",
    p: 1,
    bgcolor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
    width: 232,
    boxSizing: "border-box",
  },
  swatch: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    border: "1px solid rgba(0,0,0,0.15)",
    cursor: "pointer",
    p: 0,
  },
  swatchOn: {
    boxShadow: "0 0 0 2px white, 0 0 0 4px #2196F3",
  },
  swatchNone: {
    bgcolor: "#FFFFFF",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: "\"\"",
      position: "absolute",
      left: "50%",
      top: -2,
      bottom: -2,
      width: "1.5px",
      bgcolor: "#F44559",
      transform: "rotate(45deg)",
    },
  },
  swatchAdd: {
    border: "3px solid transparent",
    background: "linear-gradient(#FFF, #FFF) padding-box, conic-gradient(#F00, #FF0, #0F0, #0FF, #00F, #F0F, #F00) border-box",
    position: "relative",
    "&::after": {
      content: "\"+\"",
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#414141",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1,
    },
  },
  colorPickerPopover: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 5,
    bgcolor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
    overflow: "hidden",
    width: 230,
    boxSizing: "border-box",
  },
  colorPickerActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    p: "0 12px 12px",
  },
  shadeSliderWrapper: {
    px: "15px",
    pb: "12px",
  },
  shadeSliderLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#757575",
    mb: "6px",
  },
  iconPickerPopover: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    zIndex: 5,
    bgcolor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
}

const EnterpriseHeaderMedia = ({ enterprise }) => {
  const dispatch = useDispatch()
  const { objectId, logo, color } = enterprise
  // logo.url (baked with the persisted color) is what's stored and what external consumers
  // use, but the tile itself live-renders the icon component so color changes preview instantly
  // instead of waiting on the Cloudinary round-trip in persistIconExport
  const hasIcon = !!(logo && logo.icon)
  const iconLogoUrl = logo?.url || null
  const LogoIconComponent = hasIcon ? getMaterialIconComponent(logo.icon) : null
  const logoUrl = logo?.publicId ? generateCloudinaryDownloadUrl(logo.publicId) : null
  const hasLogo = hasIcon || !!logoUrl
  const isSvg = logo?.format === "svg"

  const [showPalette, setShowPalette] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [draftColor, setDraftColor] = useState(color || "#000000")
  const [svgMarkup, setSvgMarkup] = useState(null)
  const [opaque, setOpaque] = useState(false)
  const [tintedSrc, setTintedSrc] = useState(null)
  // Holds the just-confirmed color between the "Valider" click and the persisted `color` prop
  // catching up (Redux update / DB save round-trip) — without it, activeColor falls back to the
  // stale `color` prop for that instant, flashing the tile back to its previous/default color.
  const [pendingColor, setPendingColor] = useState(undefined)
  const rootRef = useRef(null)

  useEffect(() => {
    setPendingColor(undefined)
  }, [color])

  // While the advanced picker is open, preview the in-progress color without persisting it
  const activeColor = showColorPicker ? draftColor : (pendingColor !== undefined ? pendingColor : color)

  useEffect(() => {
    if (!showPalette && !showIconPicker) return
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setShowPalette(false)
        setShowIconPicker(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside, true)
    return () => document.removeEventListener("mousedown", onClickOutside, true)
  }, [showPalette, showIconPicker])

  useEffect(() => {
    if (!showPalette) setShowColorPicker(false)
  }, [showPalette])

  // Fetch the raw SVG source once so its fills/strokes can be recolored inline when a tint is picked
  useEffect(() => {
    let dead = false
    setSvgMarkup(null)
    if (!logoUrl || !isSvg) return undefined

    fetch(logoUrl)
      .then((res) => res.text())
      .then((text) => { if (!dead) setSvgMarkup(text) })
      .catch(() => {})

    return () => { dead = true }
  }, [logoUrl, isSvg])

  // Opaque rasters (jpg / png without alpha) can't be CSS-masked: recolor non-white pixels via canvas instead
  useEffect(() => {
    let dead = false
    setTintedSrc(null)
    if (!logoUrl || isSvg) {
      setOpaque(false)
      return undefined
    }

    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => {
      if (dead) return
      try {
        const probe = document.createElement("canvas")
        const scale = Math.min(1, 64 / Math.max(img.width, img.height))
        probe.width = Math.max(1, Math.round(img.width * scale))
        probe.height = Math.max(1, Math.round(img.height * scale))
        const probeCtx = probe.getContext("2d")
        probeCtx.drawImage(img, 0, 0, probe.width, probe.height)
        const pixels = probeCtx.getImageData(0, 0, probe.width, probe.height).data

        let hasAlpha = false
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 250) { hasAlpha = true; break }
        }
        setOpaque(!hasAlpha)

        if (!hasAlpha && activeColor) {
          const canvas = document.createElement("canvas")
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext("2d")
          ctx.drawImage(img, 0, 0)
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data = imageData.data
          const r = parseInt(activeColor.slice(1, 3), 16)
          const g = parseInt(activeColor.slice(3, 5), 16)
          const b = parseInt(activeColor.slice(5, 7), 16)
          for (let i = 0; i < data.length; i += 4) {
            if (data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245) continue // keep white
            data[i] = r
            data[i + 1] = g
            data[i + 2] = b
          }
          ctx.putImageData(imageData, 0, 0)
          setTintedSrc(canvas.toDataURL("image/png"))
        }
      } catch (e) {
        setOpaque(false)
      }
    }
    img.src = logoUrl

    return () => { dead = true }
  }, [logoUrl, isSvg, activeColor])

  const persist = (next) => {
    const nextColor = "color" in next ? next.color : color

    if (nextColor && !HEX_COLOR_REGEX.test(nextColor)) {
      dispatch(openAppMessageSnackBar(INVALID_COLOR_MESSAGE, "error"))
      return
    }

    dispatch(updateEnterpriseLogo(objectId, {
      logo: "logo" in next ? next.logo : logo,
      color: nextColor,
    }))
  }

  const handleOpenIconPicker = () => {
    setShowPalette(false)
    setShowIconPicker(true)
  }

  const handleToggleIconPicker = () => {
    setShowPalette(false)
    setShowIconPicker((v) => !v)
  }

  // Builds the color-tinted SVG, uploads it to Cloudinary, and persists {icon, url,
  // publicId together — the tile (and any external consumer) renders logo.url directly
  const persistIconExport = async (iconName, nextColor) => {
    try {
      const svgMarkup = await buildTintedIconSvg(iconName, nextColor || "#000000")
      const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgMarkup)))}`
      const { public_id: publicId, secure_url: url } = await uploadImageFromDataUrl(dataUrl, {
        tags: ["enterprise-logo-icon"],
      })
      persist({ logo: { icon: iconName, url, publicId }, color: nextColor })
    } catch (e) {
      dispatch(openAppMessageSnackBar(ICON_EXPORT_ERROR_MESSAGE, "error"))
    }
  }

  const handleSelectIcon = (iconName) => {
    setShowIconPicker(false)
    persistIconExport(iconName, color)
  }

  const handlePickColor = (nextColor) => {
    setShowPalette(false)
    setShowColorPicker(false)
    setPendingColor(nextColor)

    if (hasIcon) {
      persistIconExport(logo.icon, nextColor)
    } else {
      persist({ color: nextColor })
    }
  }

  const handleDelete = () => {
    setShowPalette(false)
    persist({ logo: null, color: null })
  }

  const handleOpenColorPicker = (startColor) => {
    setDraftColor(startColor || color || "#000000")
    setShowColorPicker(true)
  }

  const handleConfirmCustomColor = () => handlePickColor(draftColor)

  const handleShadeChange = (newShade) => {
    const hsva = hexToHsva(draftColor)
    setDraftColor(hsvaToHex({ ...hsva, v: newShade.v }).toUpperCase())
  }

  const showTint = !!activeColor && !opaque
  const customSwatch = color && !LOGO_COLOR_SWATCHES.includes(color) ? color : null

  return (
    <Box ref={rootRef} sx={sx.tile}>
      <Box
        className="enterprise-media-tile"
        sx={{
          ...sx.tileContent,
          ...(hasLogo ? sx.tileFilled : sx.tileEmpty),
          ...(showPalette || showIconPicker ? sx.tilePaletteOpen : {}),
        }}
      >
        {hasLogo ? (
          <>
            {hasIcon ? (
              <Box sx={sx.imageSvg}>
                <Suspense fallback={<Box component="img" src={iconLogoUrl} alt="Logo de l'entreprise" sx={sx.image} />}>
                  <LogoIconComponent aria-label="Logo de l'entreprise" sx={{ color: activeColor || "#000000" }} />
                </Suspense>
              </Box>
            ) : showTint ? (
              isSvg && svgMarkup ? (
                <Box
                  component="span"
                  role="img"
                  aria-label="Logo de l'entreprise"
                  sx={sx.imageSvg}
                  dangerouslySetInnerHTML={{ __html: tintSvgMarkup(svgMarkup, activeColor) }}
                />
              ) : (
                <Box
                  component="span"
                  role="img"
                  aria-label="Logo de l'entreprise"
                  sx={{
                    ...sx.imageTinted,
                    WebkitMaskImage: `url(${logoUrl})`,
                    maskImage: `url(${logoUrl})`,
                    backgroundColor: activeColor,
                  }}
                />
              )
            ) : (
              <Box
                component="img"
                src={activeColor && tintedSrc ? tintedSrc : logoUrl}
                alt="Logo de l'entreprise"
                sx={sx.image}
              />
            )}

            <Box className="enterprise-media-overlay" sx={sx.overlay}>
              <IconButton
                size="small"
                sx={sx.overlayBtn}
                onClick={handleToggleIconPicker}
                aria-label="Changer l'icône"
                title="Changer l'icône"
              >
                <EditIcon sx={sx.overlayIcon} />
              </IconButton>
              <IconButton
                size="small"
                sx={sx.overlayBtn}
                onClick={() => setShowPalette((v) => !v)}
                aria-label="Couleur du logo"
                title="Couleur du logo"
              >
                <PaletteIcon sx={sx.overlayIcon} />
              </IconButton>
              <IconButton
                size="small"
                sx={sx.overlayBtn}
                onClick={handleDelete}
                aria-label="Retirer le logo"
                title="Retirer le logo"
              >
                <DeleteOutlineIcon sx={sx.overlayIcon} />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box component="button" type="button" sx={sx.addButton} onClick={handleOpenIconPicker} aria-label="Choisir une icône">
            <Box sx={sx.addButtonCircle}>
              <AddIcon fontSize="small" />
            </Box>
            <Typography sx={sx.addButtonLabel}>Choisir une icône</Typography>
          </Box>
        )}
      </Box>

      {showIconPicker && (
        <Box sx={sx.iconPickerPopover}>
          <EnterpriseLogoIconPicker onSelect={handleSelectIcon} />
        </Box>
      )}

      {hasLogo && showPalette && (showColorPicker ? (
        <Box sx={sx.colorPickerPopover}>
          <Chrome
            color={draftColor}
            style={{ boxShadow: "none", border: "none" }}
            inputType={ChromeInputType.HEXA}
            showAlpha={false}
            onChange={(next) => setDraftColor(next.hex.toUpperCase())}
          />
          <Box sx={sx.shadeSliderWrapper}>
            <Typography sx={sx.shadeSliderLabel}>Luminosité</Typography>
            <ShadeSlider
              hsva={hexToHsva(draftColor)}
              style={{ height: 12, borderRadius: 2 }}
              onChange={handleShadeChange}
            />
          </Box>
          <Box sx={sx.colorPickerActions}>
            <Button size="small" onClick={() => setShowColorPicker(false)}>Annuler</Button>
            <Button size="small" variant="contained" onClick={handleConfirmCustomColor}>Valider</Button>
          </Box>
        </Box>
      ) : (
        <Box sx={sx.palette}>
          <Box
            component="button"
            type="button"
            sx={{ ...sx.swatch, ...sx.swatchNone, ...(!color ? sx.swatchOn : {}) }}
            aria-label="Couleur d'origine"
            onClick={() => handlePickColor(null)}
          />
          {LOGO_COLOR_SWATCHES.map((c) => (
            <Box
              key={c}
              component="button"
              type="button"
              sx={{ ...sx.swatch, bgcolor: c, ...(color === c ? sx.swatchOn : {}) }}
              aria-label={`Logo ${c}`}
              onClick={() => handlePickColor(c)}
            />
          ))}
          {customSwatch && (
            <Box
              component="button"
              type="button"
              sx={{ ...sx.swatch, bgcolor: customSwatch, ...sx.swatchOn }}
              aria-label={`Logo ${customSwatch}`}
              onClick={() => handlePickColor(customSwatch)}
            />
          )}
          <Box
            component="button"
            type="button"
            sx={{ ...sx.swatch, ...sx.swatchAdd }}
            aria-label="Couleur personnalisée"
            onClick={() => handleOpenColorPicker()}
          />
        </Box>
      ))}
    </Box>
  )
}

export default EnterpriseHeaderMedia
